import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, eur, catLabel, reportUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Boat,
  Wallet,
  GridFour,
  Users,
  WarningCircle,
  ClockCountdown,
  ArrowRight,
  MicrosoftExcelLogo,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const Kpi = ({ icon: Icon, label, value, accent, testid, sub }) => (
  <Card className="rounded-sm border-border shadow-none" data-testid={testid}>
    <div className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider text-muted-foreground">{label}</span>
        <Icon size={18} weight="bold" className={accent} />
      </div>
      <div className="mt-3 font-head text-3xl font-extrabold tabular-nums text-[#234E7D]">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  </Card>
);

export default function Overview() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 5000,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: alerts } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    refetchInterval: 10000,
  });

  if (isLoading || !data) return <div className="p-6 text-muted-foreground">Φόρτωση...</div>;

  if (!data.per_category) return (
    <div className="p-6 text-muted-foreground" data-testid="overview-empty">Δεν έχουν οριστεί τα στοιχεία της εγκατάστασής σας και η τιμολόγηση.</div>
  );

  const chartData = data.per_category.map((c) => ({
    name: catLabel(c.name),
    Κατειλημμένες: c.occupied,
    Κενές: c.empty,
  }));

  return (
    <div>
      <PageHeader
        title="Επισκόπηση"
        subtitle={data.name}
        action={
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => window.open(reportUrl("/reports/data.xlsx"), "_blank")}
            data-testid="overview-export-btn"
          >
            <MicrosoftExcelLogo size={18} weight="bold" className="text-[#10B981]" /> Εξαγωγή όλων των στοιχείων
          </Button>
        }
      />
      <div className="space-y-6 p-6">
        {alerts && (alerts.expired.length > 0 || alerts.expiring.length > 0) && (
          <Link to="/alerts" className="block" data-testid="overview-alerts-banner">
            <div className="flex flex-wrap items-center gap-4 rounded-sm border border-[#F59E0B]/40 bg-[#FFFBEB] px-5 py-3 text-sm transition-colors duration-150 hover:bg-[#FEF3C7]">
              {alerts.expired.length > 0 && (
                <span className="flex items-center gap-1.5 font-medium text-destructive"><WarningCircle size={16} weight="bold" /> {alerts.expired.length} ληγμένα</span>
              )}
              {alerts.expiring.length > 0 && (
                <span className="flex items-center gap-1.5 font-medium text-[#B45309]"><ClockCountdown size={16} weight="bold" /> {alerts.expiring.length} λήγουν σύντομα</span>
              )}
              <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-muted-foreground">Προβολή <ArrowRight size={14} weight="bold" /></span>
            </div>
          </Link>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi icon={GridFour} label="Πληρότητα" value={`${data.occupancy_rate}%`} accent="text-[#0055FF]" testid="kpi-occupancy" />
          <Kpi icon={Boat} label="Σκάφη" value={data.total_boats} accent="text-[#0055FF]" testid="kpi-boats" />
          <Kpi icon={Users} label="Ιδιοκτήτες" value={data.total_owners} accent="text-[#0055FF]" testid="kpi-owners" />
          <Kpi icon={Wallet} label="Υπόλοιπο οφειλών" value={eur(data.total_due)} accent="text-[#0055FF]" testid="kpi-due" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="rounded-sm border-border shadow-none lg:col-span-1" data-testid="occupancy-summary">
            <div className="border-b border-border px-5 py-4">
              <h3 className="font-head font-bold">Θέσεις</h3>
            </div>
            <div className="space-y-5 p-5">
              <div>
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-muted-foreground">Κατειλημμένες</span>
                  <span className="font-semibold tabular-nums">{data.occupied} / {data.total_slots}</span>
                </div>
                <Progress value={data.occupancy_rate} className="h-2 rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-sm border border-border p-3">
                  <div className="text-xs text-muted-foreground">Κατειλημμένες</div>
                  <div className="font-head text-2xl font-bold tabular-nums text-[#46648B]">{data.occupied}</div>
                </div>
                <div className="rounded-sm border border-border bg-[#EFF6FF] p-3">
                  <div className="text-xs text-muted-foreground">Κενές</div>
                  <div className="font-head text-2xl font-bold tabular-nums text-[#0055FF]">{data.empty}</div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-sm border-border shadow-none lg:col-span-2" data-testid="category-chart">
            <div className="border-b border-border px-5 py-4">
              <h3 className="font-head font-bold">Πληρότητα ανά κατηγορία</h3>
            </div>
            <div className="p-5">
              <ResponsiveContainer width="100%" height={220} minWidth={0}>
                <BarChart data={chartData} barGap={2}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 4, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="Κατειλημμένες" stackId="a" fill="#46648B" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Κενές" stackId="a" fill="#93C5FD" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="rounded-sm border-border shadow-none" data-testid="category-breakdown">
          <div className="border-b border-border px-5 py-4">
            <h3 className="font-head font-bold">Ανάλυση ανά κατηγορία</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs tracking-wider text-muted-foreground">
                  <th className="px-5 py-3 font-semibold">Κατηγορία</th>
                  <th className="px-5 py-3 font-semibold">Σύνολο</th>
                  <th className="px-5 py-3 font-semibold">Κατειλημμένες</th>
                  <th className="px-5 py-3 font-semibold">Κενές</th>
                  <th className="px-5 py-3 text-right font-semibold">Οφειλές</th>
                </tr>
              </thead>
              <tbody>
                {data.per_category.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0" data-testid={`cat-row-${c.id}`}>
                    <td className="px-5 py-3 font-medium">{catLabel(c.name)}</td>
                    <td className="px-5 py-3 tabular-nums">{c.total}</td>
                    <td className="px-5 py-3 tabular-nums">{c.occupied}</td>
                    <td className="px-5 py-3 tabular-nums">{c.empty}</td>
                    <td className="px-5 py-3 text-right font-semibold tabular-nums">{eur(c.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
